// LipSync Generation Modal

import React, { useState, useEffect } from 'react';
import { scenesApi, uploadApi } from '../../services/api';
import type { LipsyncSettings } from '../../types';

interface LipsyncModalProps {
  isOpen: boolean;
  projectId: string;
  sceneNumber: number;
  onClose: () => void;
  onTaskCreated: (taskId: string) => void;
}

const LipsyncModal: React.FC<LipsyncModalProps> = ({
  isOpen,
  projectId,
  sceneNumber,
  onClose,
  onTaskCreated,
}) => {
  const [model, setModel] = useState<LipsyncSettings['model']>('sync-lipsync');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [lipsyncModel, setLipsyncModel] = useState('lipsync-1.9.0-beta');
  const [syncMode, setSyncMode] = useState('cut_off');
  const [useTTS, setUseTTS] = useState(false);
  const [ttsText, setTTSText] = useState('');
  const [voice, setVoice] = useState('Auto');
  const [generating, setGenerating] = useState(false);

  // Сбросить состояние generating при открытии модала
  useEffect(() => {
    if (isOpen) {
      setGenerating(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleGenerate = async () => {
    if (!videoFile) {
      alert('Please upload a video file');
      return;
    }

    if (model === 'pixverse' && useTTS) {
      if (!ttsText.trim()) {
        alert('Please enter TTS text');
        return;
      }
    } else if (!audioFile) {
      alert('Please upload an audio file');
      return;
    }

    setGenerating(true);

    try {
      const videoUpload = await uploadApi.uploadTempFile(videoFile);
      if (!videoUpload.success) throw new Error('Video upload failed');

      let audioUrl;
      if (!useTTS && audioFile) {
        const audioUpload = await uploadApi.uploadTempFile(audioFile);
        if (!audioUpload.success) throw new Error('Audio upload failed');
        audioUrl = audioUpload.url;
      }

      const settings: LipsyncSettings = {
        model,
        video_url: videoUpload.url,
      };

      if (model === 'sync-lipsync') {
        settings.lipsync_model = lipsyncModel;
        settings.sync_mode = syncMode;
        settings.audio_url = audioUrl;
      } else if (model === 'pixverse' && useTTS) {
        settings.use_tts = true;
        settings.text = ttsText;
        settings.voice_id = voice;
      } else {
        settings.audio_url = audioUrl;
      }

      const result = await scenesApi.generateLipsync(projectId, sceneNumber, settings);

      if (result.success && result.task_id) {
        onTaskCreated(result.task_id);
        onClose();
      } else {
        alert('Failed to start lipsync generation');
      }
    } catch (error: any) {
      console.error('Error generating lipsync:', error);
      alert('Error: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={onClose}>
      <div className="modal-content wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Generate LipSync for Scene {sceneNumber}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="form-group">
          <label>Select Model:</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
            {(['sync-lipsync', 'veed', 'pixverse', 'kling'] as const).map((m) => (
              <div
                key={m}
                className={`model-card ${model === m ? 'selected' : ''}`}
                onClick={() => setModel(m)}
                style={{
                  padding: '15px',
                  border: model === m ? '2px solid #007bff' : '2px solid #dee2e6',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  textAlign: 'center',
                  background: model === m ? '#e3f2fd' : 'white',
                }}
              >
                <h5>{m === 'sync-lipsync' ? 'Sync-LipSync' : m.charAt(0).toUpperCase() + m.slice(1)}</h5>
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Upload Video:</label>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
          />
          {videoFile && <small>✓ {videoFile.name}</small>}
        </div>

        {model === 'pixverse' && (
          <div className="form-group">
            <label>
              <input type="checkbox" checked={useTTS} onChange={(e) => setUseTTS(e.target.checked)} />
              {' '}Use Text-to-Speech
            </label>
          </div>
        )}

        {model === 'pixverse' && useTTS ? (
          <>
            <div className="form-group">
              <label>TTS Text:</label>
              <textarea value={ttsText} onChange={(e) => setTTSText(e.target.value)} rows={3} />
            </div>
            <div className="form-group">
              <label>Voice:</label>
              <select value={voice} onChange={(e) => setVoice(e.target.value)}>
                {['Auto', 'Emily', 'James', 'Isabella', 'Liam', 'Chloe'].map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <div className="form-group">
            <label>Upload Audio:</label>
            <input
              type="file"
              accept="audio/*"
              onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
            />
            {audioFile && <small>✓ {audioFile.name}</small>}
          </div>
        )}

        {model === 'sync-lipsync' && (
          <div>
            <div className="form-group">
              <label>LipSync Model:</label>
              <select value={lipsyncModel} onChange={(e) => setLipsyncModel(e.target.value)}>
                <option value="lipsync-1.9.0-beta">1.9.0 Beta</option>
                <option value="lipsync-1.8.0">1.8.0</option>
              </select>
            </div>
            <div className="form-group">
              <label>Sync Mode:</label>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {['cut_off', 'loop', 'bounce', 'silence', 'remap'].map((mode) => (
                  <div
                    key={mode}
                    className={syncMode === mode ? 'selected' : ''}
                    onClick={() => setSyncMode(mode)}
                    style={{
                      padding: '8px 12px',
                      border: syncMode === mode ? '2px solid #007bff' : '2px solid #dee2e6',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      background: syncMode === mode ? '#e3f2fd' : 'white',
                    }}
                  >
                    {mode.replace('_', ' ').charAt(0).toUpperCase() + mode.slice(1).replace('_', ' ')}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '20px' }}>
          <button onClick={onClose} className="btn btn-secondary" disabled={generating}>
            Cancel
          </button>
          <button onClick={handleGenerate} className="btn btn-primary" disabled={generating}>
            {generating ? 'Generating...' : 'Generate LipSync'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LipsyncModal;
